import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { tagsApi } from '~/entities/tag/api/tags-api'
import { formatTagLabel } from '~/entities/tag/lib/tag-label'
import * as m from '~/paraglide/messages'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { Badge } from '~/shared/ui/badge'
import { Label } from '~/shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/shared/ui/select'
import { Skeleton } from '~/shared/ui/skeleton'

interface TagSelectorProps {
  selectedCategoryId?: string
  selectedSubCategoryIds?: string[]
  onCategoryChange: (categoryId: string) => void
  onSubCategoriesChange: (subCategoryIds: string[]) => void
  maxSubCategories?: number
}

export function TagSelector({
  selectedCategoryId,
  selectedSubCategoryIds = [],
  onCategoryChange,
  onSubCategoriesChange,
  maxSubCategories = 3,
}: TagSelectorProps) {
  const { data: allTags = [], isLoading } = useQuery({
    queryKey: queryKey(QueryKey.TAGS),
    queryFn: () => tagsApi.getAll(),
  })

  const mainCategories = useMemo(
    () => allTags.filter((tag) => tag.tagType === 'category'),
    [allTags],
  )

  const subCategories = useMemo(() => {
    if (!selectedCategoryId) return []
    return allTags.filter(
      (tag) => tag.tagType === 'sub_category' && tag.parentId === selectedCategoryId,
    )
  }, [allTags, selectedCategoryId])

  const handleSubCategoryToggle = (subCategoryId: string) => {
    const isSelected = selectedSubCategoryIds.includes(subCategoryId)

    if (isSelected) {
      onSubCategoriesChange(selectedSubCategoryIds.filter((id) => id !== subCategoryId))
    } else if (selectedSubCategoryIds.length < maxSubCategories) {
      onSubCategoriesChange([...selectedSubCategoryIds, subCategoryId])
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-10 w-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-48" />
          <div className="flex flex-wrap gap-2">
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-6 w-28" />
            <Skeleton className="h-6 w-22" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>{m.mainCategory()}</Label>
        <Select value={selectedCategoryId} onValueChange={onCategoryChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={m.selectACategory()} />
          </SelectTrigger>
          <SelectContent position="popper" sideOffset={5}>
            {mainCategories.map((category) => (
              <SelectItem key={category.id} value={category.id}>
                {formatTagLabel(category.name)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {selectedCategoryId && subCategories.length > 0 && (
        <div className="space-y-2">
          <Label>{m.subCategoriesSelectUpTo({ maxCount: maxSubCategories })}</Label>
          <div className="flex flex-wrap gap-2">
            {subCategories.map((subCategory) => {
              const isSelected = selectedSubCategoryIds.includes(subCategory.id)
              const isDisabled = !isSelected && selectedSubCategoryIds.length >= maxSubCategories

              return (
                <Badge
                  key={subCategory.id}
                  variant={isSelected ? 'default' : 'outline'}
                  className={`cursor-pointer transition-colors ${
                    isDisabled
                      ? 'opacity-50 cursor-not-allowed'
                      : isSelected
                        ? 'hover:bg-primary/80'
                        : 'hover:bg-secondary hover:text-secondary-foreground'
                  }`}
                  onClick={() => !isDisabled && handleSubCategoryToggle(subCategory.id)}
                >
                  {formatTagLabel(subCategory.name)}
                </Badge>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
